import { lazy } from '@attain-apps/function-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import { Expression } from '../../expression'
import type * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { standardNodeCreator } from './utils'

/**
 * Creates an object with custom fields
 *
 * @param definition An object where each field is an expression
 * @example
 * {
 *   '{}': {
 *     a: { '@a': 'name' },
 *     b: { '@': 'a' },
 *     c: {
 *       '{}': { d: { '@a': 'code' } }
 *     },
 *     e: {
 *       '[]': [ { '@': 'c' } ]
 *     }
 *   }
 * }
 * // ->
 * {
 *   a: 'Room 100',
 *   b: 'Room 100',
 *   c: { d: 'RM100' },
 *   e: [ { d: 'RM100' } ]
 * }
 */
export interface CreateObject<out T = any> {
  /** {@link CreateObject} */
  readonly [Key.CreateObject]: T extends Object.Constraint
    ? T extends readonly any[]
      ? never
      : CreateObject.Fields<T>
    : never
}
export namespace CreateObject {
  export const isCreateObject: TypeChecker<CreateObject> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<CreateObject>({
        [Key.CreateObject]: Fields.isFields,
      }),
    ).usingAlias('Fn.CreateObject')
  export const assertIsCreateObject: TypeAsserter<CreateObject> =
    TypeAsserter.Factory.from(isCreateObject)

  export type Fields<out T extends Object.Constraint = any> = {
    // NOTE: if we encounter a field that is `unknown`, we treat it as `any`
    // since that gives us a more correct type
    [K in keyof T]: Expression<unknown extends T[K] ? any : T[K]>
  }
  export namespace Fields {
    export const isFields: TypeChecker<Fields> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.record(TypeChecker.isString, Expression.isExpression),
    )
  }

  /**
   * @see {@link Node}
   */
  export class Node<
    T extends Object.Constraint = Object.Constraint,
  > extends fnNode.Node<
    T,
    Key.CreateObject,
    { readonly [K in keyof T]: expressionNode.Node<T[K]> }
  > {
    public readonly type = common.value.types.Intrinsic.Object

    constructor(arg: Node<T>['arg']) {
      super(Key.CreateObject, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<CreateObject> = {
    createNode: lazy(() =>
      standardNodeCreator.withProperties({
        fnKey: Key.CreateObject,
        isFn: isCreateObject,
        nodeClass: Node,
      }),
    ),
    expressionsInArgs: fn => Object.values(fn[Key.CreateObject]),
    typeChecker: isCreateObject,
  }
}
